import { defineStore } from "pinia";
import { rootGetters } from "@/main";
import { Keys } from "./catalog_data";
import { Endpoints } from "./catalog_data";
import { sortArrayByFieldWithPrefered, mergeArrays, getName } from "@/services/utils/Arrays";

const perPageDefault = 50;

export function createStoreConfig<T>(endpoints: Endpoints, entity: string, keys: Keys) {
    return defineStore(entity + "s", {
        state: () => ({
            itemList: [] as T[],
            page: 0,
            pageWithFilter: 0,
            filter: "",
            count: 1,
            countPreferred: 0,
            isLoadingInitial: false,
            initialLoadPromise: null as Promise<number> | null,
            newItemsPks: new Set<string>(), // Храним PK новых элементов
        }),
        getters: {
            itemsWithName: (state) => {
                if (!("nameKeys" in keys)) return state.itemList;
                return state.itemList.map(e => ({
                    ...e,
                    name: getName(e, keys["nameKeys"])
                }));
            },
        },
        actions: {
            // Проверяет, валиден ли preferredOrder для загрузки
            isValidPreferredOrder(preferredOrder: string[]): boolean {
                return preferredOrder.length > 0 &&
                    preferredOrder.join(",") != "" &&
                    preferredOrder.join(",") != "0";
            },

            // Находит недостающие элементы из preferredOrder в itemList
            findMissingPreferred(preferredOrder: string[]): string[] {
                return preferredOrder.filter((pk) => {
                    return !this.itemList.some(
                        (item: any) => String(item[keys.pk]) === String(pk)
                    );
                });
            },

            // Обрабатывает загрузку preferred элементов
            async handlePreferredLoad(
                preferredOrder: string[],
                need_sort: boolean
            ): Promise<number | null> {
                const missingPreferred = this.findMissingPreferred(preferredOrder);

                // Если все элементы уже есть - ничего не загружаем, только сортируем
                if (missingPreferred.length === 0) {
                    if (need_sort) {
                        this.sortItems(preferredOrder);
                    }
                    return 0;
                }

                // Если список не пустой - загружаем только недостающие элементы
                if (this.itemList.length > 0) {
                    this.countPreferred = 1;
                    await this.getPreferred(missingPreferred);
                    if (need_sort) {
                        this.sortItems(preferredOrder);
                    }
                    return missingPreferred.length;
                }

                // Если список пустой - нужно загрузить общий список сначала
                return null;
            },

            // Формирует параметры запроса
            buildRequestParams(
                pattern__ilike: string,
                preferredOrder: string[],
                per_page: number
            ): any {
                const params: any = {
                    page: this.page + 1,
                    per_page: per_page,
                    order_by: keys.order_by,
                    ...(keys.defaultParams || {}),
                };

                if (this.countPreferred > 0) {
                    if (preferredOrder.length > 0) {
                        params[keys.in] = preferredOrder.join(",");
                    }
                    params.page = this.countPreferred;
                }

                if (pattern__ilike) {
                    params[keys.ilike] = pattern__ilike;
                    params.page = this.pageWithFilter + 1;
                }

                return params;
            },

            // Обновляет состояние пагинации после загрузки
            updatePagination(pattern__ilike: string, page: number): void {
                if (pattern__ilike) {
                    this.pageWithFilter = page;
                } else {
                    this.page = page;
                }
            },

            // Обрабатывает ответ после загрузки данных
            async processResponse(
                response: any,
                get_preferred: boolean,
                preferredOrder: string[],
                pattern__ilike: string,
                need_sort: boolean,
                params: any
            ): Promise<number> {
                if (!response.data) {
                    return 0;
                }

                if (response.data.length) {
                    this.updatePagination(pattern__ilike, params.page);
                }

                // Сохраняем статус новых элементов перед объединением
                const preservedNewItemsPks = new Set(this.newItemsPks);

                this.itemList = mergeArrays(keys.pk, this.itemList, response.data);

                // Восстанавливаем статус новых элементов после объединения
                this.newItemsPks = preservedNewItemsPks;

                // Если get_preferred = true и список был пустой, загружаем недостающие preferred
                if (get_preferred && this.isValidPreferredOrder(preferredOrder) && !pattern__ilike) {
                    const missingPreferred = this.findMissingPreferred(preferredOrder);
                    if (missingPreferred.length > 0) {
                        this.countPreferred = 1;
                        await this.getPreferred(missingPreferred);
                    }
                }

                if (need_sort) {
                    this.sortItems(preferredOrder);
                }

                return response.data.length;
            },

            async getItems(
                pattern__ilike = "",
                preferredOrder: string[] = [],
                need_sort = true,
                get_preferred = false,
                page = 0,
                per_page = 50,
                isInitialLoad = false
            ) {
                // Если это начальная загрузка общего списка (без фильтров и preferred)
                const isGeneralInitialLoad = isInitialLoad &&
                    !pattern__ilike &&
                    !get_preferred &&
                    this.itemList.length === 0;

                // Если начальная загрузка уже идет, ждем ее завершения
                if (isGeneralInitialLoad && this.isLoadingInitial && this.initialLoadPromise) {
                    return await this.initialLoadPromise;
                }

                // Если get_preferred = true и есть preferredOrder, проверяем наличие элементов
                if (get_preferred && this.isValidPreferredOrder(preferredOrder) && !pattern__ilike) {
                    const result = await this.handlePreferredLoad(preferredOrder, need_sort);
                    // Если вернулся результат (не null), значит уже обработали
                    if (result !== null) {
                        return result;
                    }
                    // Если null - список был пустой, продолжаем загрузку общего списка
                }

                // Если это начальная загрузка общего списка, устанавливаем флаг
                if (isGeneralInitialLoad && !this.isLoadingInitial) {
                    this.isLoadingInitial = true;
                }

                // Обновляем фильтр при необходимости
                if (pattern__ilike != this.filter) {
                    this.filter = pattern__ilike;
                    this.pageWithFilter = 0;
                }

                // Формируем параметры запроса
                const params = this.buildRequestParams(pattern__ilike, preferredOrder, per_page);

                // Выполняем запрос и обрабатываем ответ
                const requestPromise = rootGetters.api.proceedRequest(endpoints.getItems, {}, params)
                    .then(async (response) => {
                        const result = await this.processResponse(
                            response,
                            get_preferred,
                            preferredOrder,
                            pattern__ilike,
                            need_sort,
                            params
                        );

                        // Сбрасываем флаг начальной загрузки после успешной загрузки
                        if (isGeneralInitialLoad) {
                            this.isLoadingInitial = false;
                            this.initialLoadPromise = null;
                        }

                        return result;
                    })
                    .catch((error) => {
                        // Сбрасываем флаг при ошибке
                        if (isGeneralInitialLoad) {
                            this.isLoadingInitial = false;
                            this.initialLoadPromise = null;
                        }
                        throw error;
                    });

                // Сохраняем Promise для начальной загрузки
                if (isGeneralInitialLoad && !this.initialLoadPromise) {
                    this.initialLoadPromise = requestPromise;
                }

                return await requestPromise;
            },
            sortItems(preferredOrder: string[]) {
                // Разделяем элементы на новые и остальные
                const newItems: T[] = [];
                const otherItems: T[] = [];

                this.itemList.forEach((item: any) => {
                    const itemPk = String(item[keys.pk]);
                    if (this.newItemsPks.has(itemPk)) {
                        newItems.push(item);
                    } else {
                        otherItems.push(item);
                    }
                });

                // Сортируем только остальные элементы
                const sortedOtherItems = sortArrayByFieldWithPrefered(
                    otherItems,
                    preferredOrder,
                    keys.pk,
                    keys.sortOptions
                );

                // Новые элементы остаются в начале, остальные после них
                (this.itemList as any[]) = [...newItems, ...sortedOtherItems];
            },
            async getPreferred(preferredOrder: string[]) {
                while (this.countPreferred > 0) {
                    const p = await this.getItems("", preferredOrder, false, false, this.count);
                    this.countPreferred = p && p >= perPageDefault ? this.countPreferred + 1 : 0;
                }
            },

            // Добавляет или обновляет элемент в списке
            addOrUpdateItem(item: T, needSort = true): void {
                const itemPk = String((item as any)[keys.pk]);
                const index = this.itemList.findIndex(
                    (existingItem: any) => String(existingItem[keys.pk]) === itemPk
                );

                if (index >= 0) {
                    // Обновляем существующий элемент
                    (this.itemList as any[])[index] = item as any;
                    // Применяем сортировку для обновленного элемента
                    if (needSort && keys.sortOptions) {
                        this.sortItems([]);
                    }
                } else {
                    // Добавляем новый элемент в начало списка
                    (this.itemList as any[]).unshift(item as any);
                    // Помечаем как новый элемент
                    this.newItemsPks.add(itemPk);
                    // Для нового элемента не применяем сортировку, оставляем в начале
                }
            },

            // Обновляет список, сбрасывая кеш и загружая заново
            async refreshItems(preferredOrder: string[] = []): Promise<void> {
                this.page = 0;
                this.itemList = [];
                this.newItemsPks.clear();
                this.isLoadingInitial = false;
                this.initialLoadPromise = null;
                this.countPreferred = 0;
                await this.getItems("", preferredOrder, true, false, 0, 50, true);
            }
        }
    });
}